/**
 * The standard states (design 11 §4): loading (skeletons), empty (with the next action), error
 * (the message, request id and docs link), permission denied (which roles have access).
 */
import { AlertTriangle, Lock, Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import { ApiError } from "../api/client";
import { useT } from "../i18n";
import { Button } from "./Button";

export function Loading({ label }: { label: string }) {
  return (
    <div role="status" aria-live="polite" aria-busy="true" className="space-y-3 p-2">
      <span className="sr-only">{label}</span>
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-10 animate-pulse rounded-md bg-[var(--color-surface)]" />
      ))}
    </div>
  );
}

function Panel({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="mx-auto max-w-xl rounded-lg border border-[var(--color-border)] p-8 text-center">
      <div className="mb-3 flex justify-center text-[var(--color-muted)]" aria-hidden="true">
        {icon}
      </div>
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="mt-2 space-y-3 text-[var(--color-muted)]">{children}</div>
    </section>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <Panel icon={<Sparkles size={28} />} title={title}>
      {children}
    </Panel>
  );
}

export function Soon({ release }: { release: string }) {
  const t = useT();
  return (
    <Panel icon={<Sparkles size={28} />} title={t("state.soon.title", { release })}>
      <p>{t("state.soon.body", { release })}</p>
    </Panel>
  );
}

export function PermissionDenied({ detail, role, tenant }: { detail?: string; role: string; tenant: string }) {
  const t = useT();
  return (
    <Panel icon={<Lock size={28} />} title={t("state.denied.title")}>
      <p>{t("state.denied.body", { role, tenant })}</p>
      {detail && <p className="font-mono text-sm">{detail}</p>}
    </Panel>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const t = useT();
  const problem = error instanceof ApiError ? error.problem : null;
  return (
    <div role="alert">
      <Panel icon={<AlertTriangle size={28} className="text-[var(--color-danger)]" />} title={t("state.error.title")}>
        <p>{problem?.detail ?? problem?.title ?? (error instanceof Error ? error.message : String(error))}</p>
        {problem?.request_id && <p className="font-mono text-sm">{t("state.error.requestId", { id: problem.request_id })}</p>}
        <div className="flex justify-center gap-3">
          {problem?.docs_url && (
            <a className="underline text-[var(--color-primary)]" href={problem.docs_url} target="_blank" rel="noreferrer">
              {t("state.error.docs")}
            </a>
          )}
          {onRetry && (
            <Button variant="secondary" onClick={onRetry}>
              {t("state.error.retry")}
            </Button>
          )}
        </div>
      </Panel>
    </div>
  );
}

/** What QueryView needs of a query (a plain or a paged one). */
export type Queried<T> =
  | { isPending: true; isError: false; data: undefined; error: unknown; refetch: () => unknown }
  | { isPending: false; isError: true; data: T | undefined; error: unknown; refetch: () => unknown }
  | { isPending: false; isError: false; data: T; error: unknown; refetch: () => unknown };

/** A query's loading, error or permission-denied state, else its data rendered. */
export function QueryView<T>({
  query,
  denied,
  children,
}: {
  query: Queried<T>;
  denied: { role: string; tenant: string };
  children: (data: T) => ReactNode;
}) {
  const t = useT();
  if (query.isPending) return <Loading label={t("app.loading")} />;
  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 403)
      return <PermissionDenied detail={query.error.problem.detail} {...denied} />;
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  return <>{children(query.data as T)}</>;
}
